import { TemplateRef, Type } from '@angular/core';
import { FormControl, ValidatorFn } from '@angular/forms';
import { EWidget, IWorkflowStepBase, TWidgets } from 'scw-digital-boards-widgets';
import { EDialogWidth } from '../../../core/constants/ui.constants';
import { DashboardRefreshIntervalService } from '../../../core/services/dashboard-refresh-interval.service';
import { IEntityTranslation } from '../../../core/services/entity-translator-service';
import { IWorkspaceModules } from '../../settings/dashboards/dashboards.interface';
import { EDashboardLayout, IWidgetData } from '../dashboard-content.interface';
import { IConfigurationsOfAdvancedFilterInputs } from './input-components/configure-filters-of-a-mode-panel/configure-filters-of-a-mode-panel.interface';

export enum ETypeDependentInputs {
  text = 'text',
  text2 = 'text2',
  isDateShownSingleSelectDropdown = 'isDateShownSingleSelectDropdown',
  shiftSelectionDropdown = 'shiftSelectionDropdown',
  ratioWidgetDisplayType = 'ratioWidgetDisplayType',
  arithmeticOperatorDropdown = 'arithmeticOperatorDropdown',
  woOEEMetricLayoutMultiSelectDropdown = 'woOEEMetricLayoutMultiSelectDropdown',
  woOEEChartTypeSingleSelectDropdown = 'woOEEChartTypeSingleSelectDropdown',
  checkInLogsGroupingSingleSelectDropdown = 'checkInLogsGroupingSingleSelectDropdown',
  checkInLogsDisplayConfigurationMultiSelectDropdown = 'checkInLogsDisplayConfigurationMultiSelectDropdown',
  laborEfficiencyUnitDropdown = 'laborEfficiencyUnitDropdown',
  laborEfficiencyModeDropdown = 'laborEfficiencyModeDropdown',
  nextFiveWorkOrderCountSingleSelectDropdown = 'nextFiveWorkOrderCountSingleSelectDropdown',
  nextFiveWorkOrderListFieldOneSingleSelectDropdown = 'nextFiveWorkOrderListFieldOneSingleSelectDropdown',
  nextFiveWorkOrderListFieldTwoSingleSelectDropdown = 'nextFiveWorkOrderListFieldTwoSingleSelectDropdown',
  nextFiveWorkOrderListFieldThreeSingleSelectDropdown = 'nextFiveWorkOrderListFieldThreeSingleSelectDropdown',
  nextFiveWorkOrderListFieldFourSingleSelectDropdown = 'nextFiveWorkOrderListFieldFourSingleSelectDropdown',
  url = 'url',
  showThumbnailCheckbox = 'showThumbnailCheckbox',
  fitImageToWidgetCheckbox = 'fitImageToWidgetCheckbox',
  displayIntervalInput = 'displayIntervalInput',
  comparisonSourceSelectDropdown = 'comparisonSourceSelectDropdown',
  fromLastNDays = 'fromLastNDays',
  widgetRequestSingleSelectDropdown = 'widgetRequestSingleSelectDropdown',
  widgetRequestSingleSelectDropdown2 = 'widgetRequestSingleSelectDropdown2',
  widgetRequestCategorySingleSelectDropdown = 'widgetRequestCategorySingleSelectDropdown',
  widgetRequestCategorySingleSelectDropdown2 = 'widgetRequestCategorySingleSelectDropdown2',
  formSingleSelectDropdown = 'formSingleSelectDropdown',
  formAssociation = 'formAssociation',
  thresholdBasedColoringSelection = 'thresholdBasedColoringSelection',
  imageUpload = 'imageUpload',
  escalationStatusSingleSelectDropdown = 'escalationStatusSingleSelectDropdown',
  resultAggregation = 'resultAggregation',
  formDataType = 'formDataType',
  formField = 'formField',
  itemCategoryShiftConfigurations = 'itemCategoryShiftConfigurations',
  excludeFutureDatesFromStatusColoringCheckbox = 'excludeFutureDatesFromStatusColoringCheckbox',
  datatableConfiguration = 'datatableConfiguration',
  tableMapping = 'tableMapping',
  formAutofillSingleSelectDropdown = 'formAutofillSingleSelectDropdown',
  endpointParameterMultiSelectDropdown = 'endpointParameterMultiSelectDropdown',
  sortBySingleSelectDropdown = 'sortBySingleSelectDropdown',
  oeeCalculationLevelSingleSelectDropdown = 'oeeCalculationLevelSingleSelectDropdown',
}

export type TRefreshIntervalOption = DashboardRefreshIntervalService['refreshDataIntervalOptions'][number]['id'];
export type TRefreshIntervalDropdownOption = DashboardRefreshIntervalService['refreshDataIntervalOptions'][number];

export interface IWidgetDefaultSize {
  w: number;
  h: number;
}

export interface IWidgetBasedConstant {
  component: Type<TWidgets>; // Do not transfer this to widgets project. It causes an error.
  defaultSize: Record<EDashboardLayout, IWidgetDefaultSize>;
  descriptionTranslateKey: string;
  isAutoScrollChecked?: boolean;
  refreshIntervalOptions: TRefreshIntervalOption[] | null;
  relatedModules: readonly (keyof IWorkspaceModules)[];
  visibleInputsInOrder: (
    | {
        type: ETypeDependentInputs;
        rules: ValidatorFn[];
      }
    | { type: 'filterSection' }
  )[];
}

export interface IAddEditDialogInjectData<Widget extends EWidget = EWidget> {
  editData: IWidgetData | null;
  layout: EDashboardLayout;
  type: Widget;
}

export interface ILine {
  id: number;
  title: string;
  siteId: number;
  workOrderId: number | null;
}

export interface IItemCategory {
  id: number;
  isActive: boolean;
  isDefault: boolean;
  name: string;
}

export type TWorkflowStep = IWorkflowStepBase;

export interface ISite {
  id: number;
  name: string;
}

export interface IJob {
  id: number;
  jobName: string;
  siteId?: number;
}

export interface IForms {
  id: number;
  name: string;
  isActive: boolean;
}

export interface IWidgetBasedInput {
  template: TemplateRef<any>;
  formControl?: FormControl<unknown>;
  widgetSpecificContext?: object;
}

export interface IBoardItemConfiguration {
  id: number;
  boardId: number;
  itemType: string;
  itemCategory: IItemCategory;
}

export enum EItemType {
  action = 'action',
  issue = 'issue',
}

export type TSubDashboardWindowSize = EDialogWidth.small | EDialogWidth.medium | EDialogWidth.large | EDialogWidth.xl;

export interface IFilterPanel {
  filters: IConfigurationsOfAdvancedFilterInputs[];
  modeName: string;
  matchingFilterSuppression: string[];
}

export interface IActivity {
  id: number;
  name: string;
  entityTranslations: IEntityTranslation[];
}

export interface ITask {
  id: number;
  title: string;
  entityTranslations: IEntityTranslation[];
}

export enum EWidgetSortBy {
  createdAtAsc = 'createdAtAsc',
  createdAtDesc = 'createdAtDesc',
  dueDateAsc = 'dueDateAsc',
  dueDateDesc = 'dueDateDesc',
  eventDateAsc = 'eventDateAsc',
  eventDateDesc = 'eventDateDesc',
}
